import React, { useState, useEffect } from 'react';
import './ActivityManagement.css';
import ActivityCard from '../components/ActivityCard';
import MultiSelectSkills from '../components/MultiSelectSkills';
import AILoadingState from '../components/AILoadingState';
import { activityTasksApi, activityAssignmentsApi, skillMastersApi, usersApi } from '../services/api';

const ActivityManagement = ({ user }) => {
  const [activityTasks, setActivityTasks] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTaskAssignments, setSelectedTaskAssignments] = useState([]);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
  const [activityAssignments, setActivityAssignments] = useState({});

  // Create Activity Form State
  const [newActivity, setNewActivity] = useState({
    name: '',
    description: '',
    objectives: '',
    genericDifficulty: 3,
    estimatedHours: 1,
    prerequisites: '',
    resources: ''
  });
  const [selectedRequiredSkills, setSelectedRequiredSkills] = useState([]);
  const [skillRequirements, setSkillRequirements] = useState({});

  // Assignment Form State
  const [selectedUserId, setSelectedUserId] = useState('');
  const [assignerNotes, setAssignerNotes] = useState('');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Get organization ID using same pattern as UsersManagement
      const organizationId =
        user?.profile?.organisationId ||
        user?.profile?.organizationId ||
        user?.profile?.subscriptionId ||
        user?.profile?.subscription_id ||
        user?.organisationId ||
        user?.organizationId ||
        'df51816f-d87a-46df-97be-a5aa49e34fb6'; // Fallback to organization ID

      const [tasksData, skillsData, usersData] = await Promise.all([
        activityTasksApi.getAll(),
        skillMastersApi.getAll(),
        usersApi.getAll(organizationId)
      ]);

      setActivityTasks(tasksData);
      setAvailableSkills(skillsData);
      setUsers(usersData);

      // Load all assignments and organize by activity task ID
      await loadAllAssignments(usersData);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAllAssignments = async (usersData) => {
    try {
      // Load ALL assignments (for managers to see all assignments across all users)
      console.log('Loading all assignments');

      const allAssignments = await activityAssignmentsApi.getAll();

      console.log('Loaded', allAssignments.length, 'assignments');

      // Group assignments by activity task ID
      const assignmentsByTask = {};
      allAssignments.forEach(assignment => {
        const taskId = assignment.activityTaskId;
        if (!assignmentsByTask[taskId]) {
          assignmentsByTask[taskId] = [];
        }
        assignmentsByTask[taskId].push(assignment);
      });

      setActivityAssignments(assignmentsByTask);
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  };

  const handleCreateActivity = () => {
    setNewActivity({
      name: '',
      description: '',
      objectives: '',
      genericDifficulty: 3,
      estimatedHours: 1,
      prerequisites: '',
      resources: ''
    });
    setSelectedRequiredSkills([]);
    setSkillRequirements({});
    setShowCreateModal(true);
  };

  const handleSubmitActivity = async () => {
    try {
      if (!newActivity.name || !newActivity.description) {
        alert('Please fill in required fields');
        return;
      }

      const activityData = {
        ...newActivity,
        estimatedHours: parseFloat(newActivity.estimatedHours)
      };

      const createdActivity = await activityTasksApi.create(activityData);

      // Add required skills
      for (const skill of selectedRequiredSkills) {
        const requirement = skillRequirements[skill.id] || { minimumProficiency: 3, isRequired: true };
        await activityTasksApi.addRequiredSkill(createdActivity.id, {
          skillId: skill.id,
          minimumProficiency: requirement.minimumProficiency,
          isRequired: requirement.isRequired,
          notes: requirement.notes || ''
        });
      }

      alert('Activity created successfully!');
      setShowCreateModal(false);
      loadData();
    } catch (error) {
      console.error('Error creating activity:', error);
      alert('Failed to create activity: ' + error.message);
    }
  };

  const handleAssignClick = (activity) => {
    setSelectedTask(activity);
    setSelectedUserId('');
    setAssignerNotes('');
    setDueDate('');
    setAiAnalysisResult(null);
    setShowAssignModal(true);
  };

  const handleViewAssignments = (activity) => {
    setSelectedTask(activity);
    const assignments = activityAssignments[activity.id] || [];
    setSelectedTaskAssignments(assignments);
    setShowAssignmentsModal(true);
  };

  const handleDeleteActivity = async (activity) => {
    const assignments = activityAssignments[activity.id] || [];
    const hasActiveAssignments = assignments.some(a => a.status !== 1); // 1 = Pending

    if (hasActiveAssignments) {
      alert('Cannot delete activity with active assignments. Only activities with no assignments or pending assignments can be deleted.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${activity.name}"?`)) {
      return;
    }

    try {
      await activityTasksApi.delete(activity.id);
      alert('Activity deleted successfully!');
      await loadData();
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Failed to delete activity: ' + error.message);
    }
  };

  const handleAnalyzeUser = async () => {
    if (!selectedUserId) {
      alert('Please select a user');
      return;
    }

    try {
      setAiAnalyzing(true);
      const analysis = await activityAssignmentsApi.analyzeUserFit(selectedUserId, selectedTask.id);

      // Parse the fullAnalysisJson to extract detailed fields
      if (analysis.fullAnalysisJson) {
        try {
          const parsedAnalysis = JSON.parse(analysis.fullAnalysisJson);

          // Extract the response JSON from the Claude response
          if (parsedAnalysis.response) {
            // The response contains a JSON string wrapped in ```json``` markers
            const jsonMatch = parsedAnalysis.response.match(/```json\n([\s\S]*?)\n```/);
            if (jsonMatch && jsonMatch[1]) {
              const detailedData = JSON.parse(jsonMatch[1]);

              // Add the detailed fields to the analysis result
              analysis.detailed_assessment = detailedData.detailed_assessment;
              analysis.experience_gaps = detailedData.experience_gaps;
              analysis.missing_skills = detailedData.missing_skills;
              analysis.concerns = detailedData.concerns || analysis.concerns;
              analysis.success_factors = detailedData.success_factors;
            }
          }
        } catch (parseError) {
          console.error('Error parsing fullAnalysisJson:', parseError);
        }
      }

      setAiAnalysisResult(analysis);
    } catch (error) {
      console.error('Error analyzing user:', error);
      alert('Failed to analyze user fit: ' + error.message);
    } finally {
      setAiAnalyzing(false);
    }
  };

  const handleConfirmAssignment = async () => {
    try {
      const assignmentData = {
        activityTaskId: selectedTask.id,
        assignedToUserId: selectedUserId,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
        assignerNotes,
        aiAnalysisJson: aiAnalysisResult?.fullAnalysisJson,
        aiEstimatedDifficulty: aiAnalysisResult?.userSpecificDifficulty,
        aiEstimatedHours: aiAnalysisResult?.estimatedTimeHours,
        aiFeasibilityScore: aiAnalysisResult?.feasibilityScore
      };

      await activityAssignmentsApi.create(assignmentData);
      alert('Activity assigned successfully!');
      setShowAssignModal(false);
    } catch (error) {
      console.error('Error creating assignment:', error);
      alert('Failed to create assignment: ' + error.message);
    }
  };

  const getRecommendationBadgeClass = (recommendation) => {
    const classes = {
      'GO': 'recommendation-go',
      'NO_GO': 'recommendation-no-go',
      'CAUTION': 'recommendation-caution'
    };
    return classes[recommendation] || 'recommendation-caution';
  };

  if (loading) {
    return <div className="activity-management-loading">Loading...</div>;
  }

  return (
    <div className="activity-management-container">
      <div className="activity-management-header">
        <h1>Activity Management</h1>
        <button className="btn-create-activity" onClick={handleCreateActivity}>
          + Create Activity
        </button>
      </div>

      <div className="activity-tasks-grid">
        {activityTasks.length === 0 ? (
          <div className="no-activities">
            <p>No activity tasks available. Create your first activity to get started!</p>
          </div>
        ) : (
          (() => {
            // Helper function to determine activity status priority for sorting
            const getActivityStatusPriority = (task) => {
              const assignments = activityAssignments[task.id] || [];

              if (assignments.length === 0) {
                return 0; // Unassigned - highest priority
              }

              const hasInProgress = assignments.some(a => a.status === 2); // InProgress
              const hasPending = assignments.some(a => a.status === 1); // Pending
              const allCompleted = assignments.length > 0 && assignments.every(a => a.status === 3); // Completed

              if (hasInProgress || hasPending) {
                return 1; // Active assignments - medium priority
              }

              if (allCompleted) {
                return 2; // All completed - lowest priority
              }

              return 1; // Default to medium priority
            };

            // Helper function to get assignment status label
            const getAssignmentStatusLabel = (task) => {
              const assignments = activityAssignments[task.id] || [];

              if (assignments.length === 0) {
                return null;
              }

              const hasInProgress = assignments.some(a => a.status === 2);
              const hasPending = assignments.some(a => a.status === 1);
              const allCompleted = assignments.length > 0 && assignments.every(a => a.status === 3);

              if (allCompleted) {
                return { label: 'All Completed', className: 'assignment-status-completed' };
              }
              if (hasInProgress) {
                return { label: 'In Progress', className: 'assignment-status-in-progress' };
              }
              if (hasPending) {
                return { label: 'Pending', className: 'assignment-status-pending' };
              }

              return null;
            };

            // Sort activities by status priority
            const sortedTasks = [...activityTasks].sort((a, b) => {
              return getActivityStatusPriority(a) - getActivityStatusPriority(b);
            });

            return sortedTasks.map((task) => {
              const assignments = activityAssignments[task.id] || [];
              const hasAssignments = assignments.length > 0;
              const hasActiveAssignments = assignments.some(a => a.status !== 1); // Not pending
              const assignmentStatus = getAssignmentStatusLabel(task);

              // Check if current user created this activity
              const userEmail = user?.profile?.email || user?.email;
              const creatorEmail = task.createdBy?.emailAddress;
              const canDelete = creatorEmail && userEmail &&
                               creatorEmail.toLowerCase() === userEmail.toLowerCase() &&
                               !hasActiveAssignments;

              return (
                <div key={task.id} className="activity-card-wrapper">
                  {assignmentStatus && (
                    <div className={`assignment-status-badge ${assignmentStatus.className}`}>
                      {assignmentStatus.label}
                    </div>
                  )}
                  <ActivityCard
                    activity={task}
                    mode="assign"
                    hasAssignments={hasAssignments}
                    assignmentCount={assignments.length}
                    canDelete={canDelete}
                    onAssignClick={handleAssignClick}
                    onViewAssignments={handleViewAssignments}
                    onDeleteActivity={handleDeleteActivity}
                  />
                </div>
              );
            });
          })()
        )}
      </div>

      {/* Create Activity Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Activity</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name <span className="required-field">*</span></label>
                <input
                  type="text"
                  value={newActivity.name}
                  onChange={(e) => setNewActivity({ ...newActivity, name: e.target.value })}
                  placeholder="e.g., Build a Drone"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description <span className="required-field">*</span></label>
                <textarea
                  value={newActivity.description}
                  onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                  placeholder="Describe the activity..."
                  rows={4}
                  required
                />
              </div>

              <div className="form-group">
                <label>Objectives <span className="optional-field">(Optional)</span></label>
                <textarea
                  value={newActivity.objectives}
                  onChange={(e) => setNewActivity({ ...newActivity, objectives: e.target.value })}
                  placeholder="What should participants achieve?"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Generic Difficulty: {newActivity.genericDifficulty} <span className="optional-field">(Optional)</span></label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={newActivity.genericDifficulty}
                    onChange={(e) => setNewActivity({ ...newActivity, genericDifficulty: parseInt(e.target.value) })}
                    className="difficulty-slider"
                  />
                  <div className="slider-labels">
                    <span>Very Easy</span>
                    <span>Very Hard</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Estimated Hours <span className="optional-field">(Optional)</span></label>
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={newActivity.estimatedHours}
                    onChange={(e) => setNewActivity({ ...newActivity, estimatedHours: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Prerequisites <span className="optional-field">(Optional)</span></label>
                <input
                  type="text"
                  value={newActivity.prerequisites}
                  onChange={(e) => setNewActivity({ ...newActivity, prerequisites: e.target.value })}
                  placeholder="Any prerequisites?"
                />
              </div>

              <div className="form-group">
                <label>Resources <span className="optional-field">(Optional)</span></label>
                <input
                  type="text"
                  value={newActivity.resources}
                  onChange={(e) => setNewActivity({ ...newActivity, resources: e.target.value })}
                  placeholder="Links to resources, materials, etc."
                />
              </div>

              <div className="form-group">
                <MultiSelectSkills
                  availableSkills={availableSkills}
                  selectedSkills={selectedRequiredSkills}
                  onSkillsChange={setSelectedRequiredSkills}
                  label="Required Skills (Optional)"
                  placeholder="Select skills needed for this activity..."
                />
              </div>

              {selectedRequiredSkills.length > 0 && (
                <div className="skill-requirements-section">
                  <h3>Skill Requirements</h3>
                  {selectedRequiredSkills.map((skill) => (
                    <div key={skill.id} className="skill-requirement-row">
                      <span className="skill-requirement-name">{skill.name}</span>
                      <div className="skill-requirement-inputs">
                        <label>
                          Min Level:
                          <input
                            type="number"
                            min="1"
                            max="5"
                            value={skillRequirements[skill.id]?.minimumProficiency || 3}
                            onChange={(e) => setSkillRequirements({
                              ...skillRequirements,
                              [skill.id]: {
                                ...skillRequirements[skill.id],
                                minimumProficiency: parseInt(e.target.value)
                              }
                            })}
                          />
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={skillRequirements[skill.id]?.isRequired !== false}
                            onChange={(e) => setSkillRequirements({
                              ...skillRequirements,
                              [skill.id]: {
                                ...skillRequirements[skill.id],
                                isRequired: e.target.checked
                              }
                            })}
                          />
                          Required
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSubmitActivity}>
                Create Activity
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Activity Modal */}
      {showAssignModal && selectedTask && (
        <div className="modal-overlay" onClick={() => !aiAnalyzing && setShowAssignModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Activity: {selectedTask.name}</h2>
              {!aiAnalyzing && (
                <button className="modal-close" onClick={() => setShowAssignModal(false)}>
                  ×
                </button>
              )}
            </div>
            <div className="modal-body">
              {!aiAnalysisResult && !aiAnalyzing && (
                <>
                  <div className="form-group">
                    <label>Select User *</label>
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                    >
                      <option value="">Choose a user...</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.firstName} {u.lastName} ({u.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={assignerNotes}
                      onChange={(e) => setAssignerNotes(e.target.value)}
                      placeholder="Any notes for the assignee?"
                      rows={3}
                    />
                  </div>

                  <div className="ai-analysis-notice">
                    <p>AI analysis typically takes 20-40 seconds</p>
                  </div>
                </>
              )}

              {aiAnalyzing && (
                <AILoadingState
                  messages={[
                    "Analyzing user's skill profile...",
                    "Comparing required skills with user expertise...",
                    "Evaluating task complexity for this user...",
                    "Calculating personalized estimates...",
                    "Generating recommendations..."
                  ]}
                />
              )}

              {aiAnalysisResult && !aiAnalyzing && (
                <div className="ai-analysis-results">
                  <h3>AI Analysis Results</h3>

                  <div className={`recommendation-badge ${getRecommendationBadgeClass(aiAnalysisResult.recommendation)}`}>
                    {aiAnalysisResult.recommendation}
                  </div>

                  <div className="analysis-metrics">
                    <div className="metric-card">
                      <div className="metric-label">Skill Match</div>
                      <div className="metric-value">{aiAnalysisResult.skillGap?.overallMatchPercentage || 0}%</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">User Difficulty</div>
                      <div className="metric-value">{aiAnalysisResult.userSpecificDifficulty}/5</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Estimated Time</div>
                      <div className="metric-value">{aiAnalysisResult.estimatedTimeHours}h</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Feasibility</div>
                      <div className="metric-value">{aiAnalysisResult.feasibilityScore}%</div>
                    </div>
                  </div>

                  <div className="analysis-section">
                    <h4>Rationale</h4>
                    <p>{aiAnalysisResult.rationale}</p>
                  </div>

                  {aiAnalysisResult.strengths && aiAnalysisResult.strengths.length > 0 && (
                    <div className="analysis-section">
                      <h4>Strengths</h4>
                      <ul>
                        {aiAnalysisResult.strengths.map((strength, idx) => (
                          <li key={idx} className="strength-item">{strength}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {aiAnalysisResult.concerns && aiAnalysisResult.concerns.length > 0 && (
                    <div className="analysis-section">
                      <h4>Concerns</h4>
                      <ul>
                        {aiAnalysisResult.concerns.map((concern, idx) => (
                          <li key={idx} className="concern-item">{concern}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Detailed Assessment Section */}
                  {aiAnalysisResult.detailed_assessment && (
                    <>
                      {aiAnalysisResult.detailed_assessment.preparation_required && (
                        <div className="analysis-section">
                          <h4>Preparation Required</h4>
                          <p>{aiAnalysisResult.detailed_assessment.preparation_required}</p>
                        </div>
                      )}

                      {aiAnalysisResult.detailed_assessment.risk_factors && aiAnalysisResult.detailed_assessment.risk_factors.length > 0 && (
                        <div className="analysis-section">
                          <h4>Risk Factors</h4>
                          <ul>
                            {aiAnalysisResult.detailed_assessment.risk_factors.map((risk, idx) => (
                              <li key={idx} className="concern-item">{risk}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {aiAnalysisResult.detailed_assessment.communication_capability && (
                        <div className="analysis-section">
                          <h4>Communication Capability</h4>
                          <p>{aiAnalysisResult.detailed_assessment.communication_capability}</p>
                        </div>
                      )}

                      {aiAnalysisResult.detailed_assessment.platform_knowledge && (
                        <div className="analysis-section">
                          <h4>Platform Knowledge</h4>
                          <p>{aiAnalysisResult.detailed_assessment.platform_knowledge}</p>
                        </div>
                      )}

                      {aiAnalysisResult.detailed_assessment.presentation_confidence && (
                        <div className="analysis-section">
                          <h4>Presentation Confidence</h4>
                          <p>{aiAnalysisResult.detailed_assessment.presentation_confidence}</p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Experience Gaps */}
                  {aiAnalysisResult.experience_gaps && aiAnalysisResult.experience_gaps.length > 0 && (
                    <div className="analysis-section">
                      <h4>Experience Gaps</h4>
                      <ul>
                        {aiAnalysisResult.experience_gaps.map((gap, idx) => (
                          <li key={idx} className="concern-item">{gap}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Missing Skills */}
                  {aiAnalysisResult.missing_skills && aiAnalysisResult.missing_skills.length > 0 && (
                    <div className="analysis-section">
                      <h4>Missing Skills</h4>
                      <ul>
                        {aiAnalysisResult.missing_skills.map((skill, idx) => (
                          <li key={idx} className="concern-item">{skill}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Success Factors */}
                  {aiAnalysisResult.success_factors && aiAnalysisResult.success_factors.length > 0 && (
                    <div className="analysis-section">
                      <h4>Success Factors</h4>
                      <ul>
                        {aiAnalysisResult.success_factors.map((factor, idx) => (
                          <li key={idx} className="strength-item">{factor}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {!aiAnalysisResult && !aiAnalyzing && (
                <>
                  <button className="btn-secondary" onClick={() => setShowAssignModal(false)}>
                    Cancel
                  </button>
                  <button className="btn-primary" onClick={handleAnalyzeUser} disabled={!selectedUserId}>
                    Analyze Fit
                  </button>
                </>
              )}
              {aiAnalysisResult && !aiAnalyzing && (
                <>
                  <button className="btn-secondary" onClick={() => setShowAssignModal(false)}>
                    Cancel
                  </button>
                  <button className="btn-primary" onClick={handleConfirmAssignment}>
                    Confirm Assignment
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Assignments Modal */}
      {showAssignmentsModal && selectedTask && (
        <div className="modal-overlay" onClick={() => setShowAssignmentsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Activity Assignments: {selectedTask.name}</h2>
              <button className="modal-close" onClick={() => setShowAssignmentsModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {selectedTaskAssignments.length === 0 ? (
                <p>No assignments found for this activity.</p>
              ) : (
                <div className="assignments-list">
                  {selectedTaskAssignments.map((assignment) => (
                    <div key={assignment.id} className="assignment-item">
                      <div className="assignment-header">
                        <h4>
                          {assignment.assignedToUser?.firstName} {assignment.assignedToUser?.lastName}
                        </h4>
                        <span className={`status-badge ${getStatusBadgeClass(assignment.status)}`}>
                          {getStatusLabel(assignment.status)}
                        </span>
                      </div>
                      <div className="assignment-details">
                        <div className="detail-row">
                          <span className="detail-label">Assigned Date:</span>
                          <span>{new Date(assignment.assignedDate).toLocaleDateString()}</span>
                        </div>
                        {assignment.dueDate && (
                          <div className="detail-row">
                            <span className="detail-label">Due Date:</span>
                            <span>{new Date(assignment.dueDate).toLocaleDateString()}</span>
                          </div>
                        )}
                        {assignment.aiEstimatedDifficulty && (
                          <div className="detail-row">
                            <span className="detail-label">AI Estimated Difficulty:</span>
                            <span>{assignment.aiEstimatedDifficulty}/5</span>
                          </div>
                        )}
                        {assignment.aiEstimatedHours && (
                          <div className="detail-row">
                            <span className="detail-label">AI Estimated Hours:</span>
                            <span>{assignment.aiEstimatedHours}h</span>
                          </div>
                        )}
                        {assignment.assignerNotes && (
                          <div className="detail-row">
                            <span className="detail-label">Notes:</span>
                            <span>{assignment.assignerNotes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAssignmentsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusBadgeClass = (status) => {
  const classes = {
    1: 'status-pending',
    2: 'status-in-progress',
    3: 'status-completed',
    4: 'status-cancelled',
    5: 'status-on-hold'
  };
  return classes[status] || 'status-pending';
};

const getStatusLabel = (status) => {
  const labels = {
    1: 'Pending',
    2: 'In Progress',
    3: 'Completed',
    4: 'Cancelled',
    5: 'On Hold'
  };
  return labels[status] || 'Unknown';
};

export default ActivityManagement;
